import { useMutation } from '@tanstack/react-query';
import { AlertCircle, MinusCircle } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { importOrgsFromV1 } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';

const formSchema = z.object({});
type Form = z.infer<typeof formSchema>;
const EMPTY_ERRORS: string[] = [];

export function ImportOrgFromV1() {
  const { toast } = useToast();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const [skipMessages, setSkipMessages] = useState<string[]>(EMPTY_ERRORS);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: importOrgsFromV1,
    mutationKey: ['ORGS'],
  });

  const onSubmit = ({ closeModal }: SubmitProps<Form>) => {
    setErrorMessages(EMPTY_ERRORS);
    mutateAsync()
      .then((summary) => {
        setErrorMessages(
          summary.failures.map(
            ({ type, kind, internalId, externalId }) =>
              `Import action ${kind} failed with entity ${type}: internalId=${internalId}, externalId=${externalId}`,
          ),
        );
        setSkipMessages(
          summary.skips.map(
            ({ type, internalId = '', externalId = '' }) =>
              `Skipped entity ${type}: internalId=${internalId}, externalId=${externalId}`,
          ),
        );

        if (summary.isCompletelySuccessful()) {
          toast({
            title: 'Import complete',
            description: `Successfully imported V1 org chart and touched ${summary.actualTotal} entities.`,
          });
          closeModal();
          return;
        }
        if (summary.isCompleteFailure()) {
          toast({
            variant: 'destructive',
            title: 'Import failed',
            description: 'See the modal for more detailed error messages.',
          });
          return;
        }
        toast({
          title: 'Import partially succeeded',
          description: `Successfully imported ${summary.succeeded} entities, but some may have skipped or failed. See the modal for more detailed messages.`,
        });
      })
      .catch((error) => {
        const airServerError = airServerErrorFrom(error);
        const errorMessage = airServerError?.userFacingMessage || 'Unknown';

        toast({
          title: 'Import org: internal error',
          description: errorMessage,
        });
      });
  };

  return (
    <FormModal
      modalTitle="Import org chart from V1 Airtable"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Import"
      trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import org chart from V1</DropdownMenuItem>}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {() => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <ol>
                <li>1. Confirm in Airtable that the orgs to import have their "Sync with V2" fields checked.</li>
                <li>
                  2. Remember that you can attempt to sync the same org again. If an existing org matches an Airtable
                  org record, their account is instead overwritten with the latest data from Airtable. No new account
                  would be created for this org.
                </li>
              </ol>
            </div>
            {isPending && <LoaderIcon />}
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Import failures</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
            {skipMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="alert">
                  <MinusCircle className="h-4 w-4" />
                  <AlertTitle>Import Skips</AlertTitle>
                  <AlertDescription>
                    {skipMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
}
